import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { Browser } from "@capacitor/browser";
import { readStoredSettings } from "@/lib/userSettings";
import { toInternalNotificationPath } from "@/lib/notificationNavigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHANNEL_ID = "lotq_general";

let listenersReady = false;
let currentAuthToken = null;
let nativeToken = null;

function getNativePushPlatform() {
  if (!Capacitor.isNativePlatform()) return null;

  const platform = Capacitor.getPlatform();
  return platform === "android" || platform === "ios" ? platform : null;
}

export function isNativePushSupported() {
  // FCM registration requires a native Firebase configuration. Keep the app
  // stable until that configuration is present in the signed Android build.
  return (
    process.env.REACT_APP_NATIVE_PUSH_ENABLED === "true" &&
    getNativePushPlatform() !== null
  );
}

async function saveToken(token) {
  nativeToken = token;
  const platform = getNativePushPlatform();
  if (!currentAuthToken || !token || !platform) return false;

  await axios.post(
    `${API}/notifications/push/native/subscribe`,
    { token, platform },
    { headers: { Authorization: `Bearer ${currentAuthToken}` } }
  );
  return true;
}

async function prepareListeners() {
  if (listenersReady) return;
  listenersReady = true;

  await PushNotifications.addListener("registration", ({ value }) => {
    saveToken(value).catch(() => {});
  });
  await PushNotifications.addListener("registrationError", () => {});
  await PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
    const target = notification?.data?.url;
    const targetPath = toInternalNotificationPath(target);
    if (targetPath) {
      window.location.assign(targetPath);
      return;
    }
    if (typeof target === "string" && /^https?:\/\//i.test(target)) {
      Browser.open({ url: target }).catch(() => window.open(target, "_blank"));
      return;
    }
    window.location.assign("/notificacoes");
  });

  await PushNotifications.createChannel({
    id: CHANNEL_ID,
    name: "Lord of the Quiz",
    description: "Avisos e novidades do Lord of the Quiz",
    importance: 4,
    visibility: 1,
    sound: "default",
    vibration: true,
  });
}

export async function getNativePushPermissionLabel() {
  if (!isNativePushSupported()) return null;
  const { receive } = await PushNotifications.checkPermissions();
  if (receive === "granted") return "Permitidas";
  if (receive === "denied") return "Bloqueadas nas configurações do app";
  return "Ainda não autorizadas";
}

export async function syncNativePushSubscription(token, { requestPermission = false } = {}) {
  if (!isNativePushSupported() || !token) return false;
  currentAuthToken = token;

  const settings = readStoredSettings();
  if (settings.browserNotifications !== true && !requestPermission) return false;

  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === "prompt" && requestPermission) {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission.receive !== "granted") return false;

  await prepareListeners();
  await PushNotifications.register();
  return true;
}

export async function disableNativePushSubscription(token) {
  if (!isNativePushSupported()) return false;

  if (token && nativeToken) {
    await axios.delete(`${API}/notifications/push/native/subscribe`, {
      headers: { Authorization: `Bearer ${token}` },
      data: { token: nativeToken },
    }).catch(() => {});
  }

  await PushNotifications.unregister().catch(() => {});
  nativeToken = null;
  currentAuthToken = null;
  return true;
}
